import { useState, useEffect } from "react";
import { clsx } from "clsx";
import {
	LOAN_PURPOSE_BUSINESS,
	LOAN_PURPOSE_PERSONAL,
	ABN_STATUS_NEW_ABN,
	EMP_STAT_SELF_EMPLOYED,
	credit_history,
	living_status,
	employment_status,
	abn_status,
	LOAN_TYPE_BUSINESS,
	gst_status,
	LOAN_PURPOSE_WORKING_CAPITAL,
	LOAN_PURPOSE_NOVATED_LEASE,
	LOAN_TYPE_PERSONAL,
	ABN_STATUS_REGISTERED,
	ABN_STATUS_UNREGISTERED,
	LOAN_PURPOSE_EQUIPMENT,
	LOAN_PURPOSE_TRUCK,
	LOAN_PURPOSE_BUSINESS_VEHICLE,
} from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";

export const AdditionalInfo = () => {
	const { formValues, setFormValue } = useFormDataStore();
	const { setFormError, enabledFields, setValidateFormDetails } = useFormStateStore();

	const {
		loanPurpose,
		livingStatus,
		employmentStatus,
		abnStatus,
		businessAbnStatus,
		gstStatus,
		creditHistory,
		combinedIncome,
		loanType,
	} = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		livingStatus: false,
		employmentStatus: false,
		abnStatus: false,
		gstStatus: false,
		creditHistory: false,
		combinedIncome: false,
	});

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormError(hasError);
	}, [errors, setFormError]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			livingStatus: enabledFields.livingStatus?.length == 0 ? false : !livingStatus,
			employmentStatus:
				enabledFields.employmentStatus?.length == 0 ||
				[LOAN_PURPOSE_BUSINESS, LOAN_PURPOSE_WORKING_CAPITAL].includes(loanPurpose)
					? false
					: !employmentStatus,
			abnStatus:
				enabledFields.abnStatus?.length == 0 ||
				[LOAN_PURPOSE_PERSONAL, LOAN_PURPOSE_NOVATED_LEASE].includes(loanPurpose) ||
				(loanPurpose === LOAN_PURPOSE_WORKING_CAPITAL &&
					businessAbnStatus === ABN_STATUS_UNREGISTERED)
					? false
					: !abnStatus,
			gstStatus:
				enabledFields.gstStatus?.length == 0 ||
				[LOAN_PURPOSE_PERSONAL, LOAN_PURPOSE_NOVATED_LEASE].includes(loanPurpose) ||
				(loanPurpose === LOAN_PURPOSE_WORKING_CAPITAL &&
					businessAbnStatus === ABN_STATUS_UNREGISTERED) ||
				abnStatus === ABN_STATUS_NEW_ABN
					? false
					: !gstStatus,
			creditHistory: enabledFields.creditHistory?.length == 0 ? false : !creditHistory,
			combinedIncome:
				loanPurpose === LOAN_PURPOSE_WORKING_CAPITAL &&
				businessAbnStatus === ABN_STATUS_UNREGISTERED
					? false
					: !combinedIncome || parseFloat(combinedIncome.replace(/,/g, "")) <= 0,
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormError(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm's Next Button can trigger this.
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [livingStatus, employmentStatus, abnStatus, gstStatus, creditHistory, combinedIncome]);

	return (
		<div className="jF-space-y-4">
			{enabledFields.livingStatus?.length != 0 && (
				<div>
					<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
						What is your current living status?
					</label>
					<select
						value={livingStatus}
						id="livingStatus"
						aria-label="Living Status"
						onChange={(e) => {
							const selectedValue = e.target.value;
							setFormValue("livingStatus", selectedValue);
							setErrors((prevErrors) => ({
								...prevErrors,
								livingStatus: !selectedValue,
							}));
						}}
						className={clsx(
							errors.livingStatus
								? "jF-ring-red-500 focus:jF-ring-red-500"
								: "jF-ring-gray-300 focus:jF-ring-gray-900",
							"jF-appearance-none jF-bg-white jF-mt-2 jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-3 jF-pr-10 jF-text-gray-900 jF-ring-1 jF-ring-inset jF-ring-gray-300 focus:jF-ring-1 focus-visible:jF-outline-[--jF-theme-color] jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
						)}
					>
						{enabledFields.livingStatus?.map((lStat) => {
							return (
								<option key={lStat.id} value={lStat.value}>
									{lStat.option}
								</option>
							);
						})}
					</select>
					{errors.livingStatus && (
						<span className="jF-text-red-500 jF-text-sm">This field is required</span>
					)}
				</div>
			)}

			{enabledFields.employmentStatus?.length != 0 &&
				formValues.loanPurpose !== LOAN_PURPOSE_BUSINESS && (
					<div>
						<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
							What is your current employment status?
						</label>
						<select
							value={employmentStatus}
							id="employmentStatus"
							aria-label="Employment Status"
							onChange={(e) => {
								const selectedValue = e.target.value;
								setFormValue("employmentStatus", selectedValue);
								setErrors((prevErrors) => ({
									...prevErrors,
									employmentStatus: !selectedValue,
								}));
							}}
							className={clsx(
								errors.employmentStatus
									? "jF-ring-red-500 focus:jF-ring-red-500"
									: "jF-ring-gray-300 focus:jF-ring-gray-900",
								"jF-appearance-none jF-bg-white jF-mt-2 jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-3 jF-pr-10 jF-text-gray-900 jF-ring-1 jF-ring-inset jF-ring-gray-300 focus:jF-ring-1 focus-visible:jF-outline-[--jF-theme-color] jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
							)}
						>
							{enabledFields.employmentStatus?.map((eStat) => {
								return (
									<option key={eStat.id} value={eStat.value}>
										{eStat.option}
									</option>
								);
							})}
						</select>
						{errors.employmentStatus && (
							<span className="jF-text-red-500 jF-text-sm">
								This field is required
							</span>
						)}
					</div>
				)}

			{enabledFields.abnStatus?.length != 0 &&
				[
					LOAN_PURPOSE_BUSINESS,
					LOAN_PURPOSE_WORKING_CAPITAL,
					LOAN_PURPOSE_BUSINESS_VEHICLE,
					LOAN_PURPOSE_EQUIPMENT,
					LOAN_PURPOSE_TRUCK,
				].includes(formValues.loanPurpose) &&
				formValues.businessAbnStatus !== ABN_STATUS_UNREGISTERED && (
					<>
						<div>
							<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
								How long has your ABN been running?
							</label>
							<select
								defaultValue={abnStatus || ""}
								id="abnStatus"
								aria-label="ABN Status"
								onChange={(e) => {
									const selectedValue = e.target.value;
									setFormValue("abnStatus", selectedValue);
									setFormValue("employmentStatus", EMP_STAT_SELF_EMPLOYED);
									setErrors((prevErrors) => ({
										...prevErrors,
										abnStatus: !selectedValue,
									}));
									if (selectedValue === ABN_STATUS_NEW_ABN) {
										setFormValue("gstStatus", "");
									}
								}}
								className={clsx(
									errors.abnStatus
										? "jF-ring-red-500 focus:jF-ring-red-500"
										: "jF-ring-gray-300 focus:jF-ring-gray-900",
									"jF-appearance-none jF-bg-white jF-mt-2 jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-3 jF-pr-10 jF-text-gray-900 jF-ring-1 jF-ring-inset jF-ring-gray-300 focus:jF-ring-1 focus-visible:jF-outline-[--jF-theme-color] jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
								)}
							>
								{enabledFields.abnStatus?.map((abnStat) => {
									return (
										<option key={abnStat.value} value={abnStat.value}>
											{abnStat.option}
										</option>
									);
								})}
							</select>
							{errors.abnStatus && (
								<span className="jF-text-red-500 jF-text-sm">
									This field is required
								</span>
							)}
						</div>
						{enabledFields.gstStatus?.length != 0 &&
							formValues.abnStatus !== ABN_STATUS_NEW_ABN &&
							formValues.businessAbnStatus !== ABN_STATUS_UNREGISTERED && (
								<div>
									<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
										How long have you been registered for GST?
									</label>
									<select
										defaultValue={gstStatus || ""}
										id="gstStatus"
										aria-label="GST Status"
										onChange={(e) => {
											const selectedValue = e.target.value;
											setFormValue("gstStatus", selectedValue);
											setErrors((prevErrors) => ({
												...prevErrors,
												gstStatus: !selectedValue,
											}));
										}}
										className={clsx(
											errors.gstStatus
												? "jF-ring-red-500 focus:jF-ring-red-500"
												: "jF-ring-gray-300 focus:jF-ring-gray-900",
											"jF-appearance-none jF-bg-white jF-mt-2 jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-3 jF-pr-10 jF-text-gray-900 jF-ring-1 jF-ring-inset jF-ring-gray-300 focus:jF-ring-1 focus-visible:jF-outline-[--jF-theme-color] jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
										)}
									>
										{enabledFields.gstStatus?.map((gstStat) => {
											return (
												<option key={gstStat.value} value={gstStat.value}>
													{gstStat.option}
												</option>
											);
										})}
									</select>
									{errors.gstStatus && (
										<span className="jF-text-red-500 jF-text-sm">
											This field is required
										</span>
									)}
								</div>
							)}
					</>
				)}

			{enabledFields.creditHistory?.length != 0 && (
				<div>
					<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
						How would you rate your credit history?
					</label>
					<select
						defaultValue={creditHistory || ""}
						id="creditHistory"
						aria-label="Credit History"
						onChange={(e) => {
							const selectedValue = e.target.value;
							setFormValue("creditHistory", selectedValue);
							setErrors((prevErrors) => ({
								...prevErrors,
								creditHistory: !selectedValue,
							}));
						}}
						className={clsx(
							errors.creditHistory
								? "jF-ring-red-500 focus:jF-ring-red-500"
								: "jF-ring-gray-300 focus:jF-ring-gray-900",
							"jF-appearance-none jF-bg-white jF-mt-2 jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-3 jF-pr-10 jF-text-gray-900 jF-ring-1 jF-ring-inset jF-ring-gray-300 focus:jF-ring-1 focus-visible:jF-outline-[--jF-theme-color] jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
						)}
					>
						{credit_history?.map((chStat) => {
							return (
								<option key={chStat.value} value={chStat.value}>
									{chStat.option}
								</option>
							);
						})}
					</select>
					{errors.creditHistory && (
						<span className="jF-text-red-500 jF-text-sm">This field is required</span>
					)}
				</div>
			)}

			{formValues.businessAbnStatus !== ABN_STATUS_UNREGISTERED && (
				<div>
					<label className="jF-block jF-text-md jF-font-medium jF-leading-6 jF-text-gray-900">
						What is your combined annual income?
					</label>
					<div className="jF-relative jF-mt-2 jF-rounded-md jF-shadow-sm">
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
							<span className="jF-text-gray-500 sm:jF-text-md">$</span>
						</div>
						<input
							value={combinedIncome || ""}
							type="text"
							id="grossIncome"
							aria-describedby="combinedIncome"
							inputMode="numeric"
							aria-label="Combined Income"
							maxLength={12}
							onChange={(e) => {
								const value = +e.target.value.replace(/[^0-9]/g, "");
								setFormValue("combinedIncome", value.toLocaleString());
								setErrors((prevErrors) => ({
									...prevErrors,
									combinedIncome: !value,
								}));
							}}
							className={clsx(
								errors.combinedIncome
									? "jF-ring-red-500 focus:jF-ring-red-500"
									: "jF-ring-gray-300 focus:jF-ring-gray-900",
								" jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-7 jF-pr-12 jF-text-gray-900 jF-ring-1 jF-ring-inset  placeholder:jF-text-gray-400 focus:jF-ring-1 focus-visible:jF-outline-[--jF-theme-color] jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6"
							)}
						/>
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-right-0 jF-flex jF-items-center jF-pr-3">
							<span className="jF-text-gray-500 sm:jF-text-sm">AUD</span>
						</div>
					</div>
					{errors.combinedIncome && (
						<span className="jF-text-xs jF-text-red-500">
							Combined Annual Income is required
						</span>
					)}
				</div>
			)}
		</div>
	);
};
